import React from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import runtimeConfig from '../../../runtime-config';
import stripePromise from '../stripe/stripe-client';
import { checkoutPayload, matchesBasket, clearAttempt, assertCurrentAttempt } from './checkout-attempt';
import { resetCart } from '../../../redux/cart/cart.actions';
import { selectCartItems, selectCartTotal } from '../../../redux/cart/cart.selectors';
import { resetCustomer } from '../../../redux/customer/customer.actions';
import { selectCurrentCustomer } from '../../../redux/customer/customer.selectors';
import { resetShipping } from '../../../redux/shipping/shipping.actions';
import { selectCurrentShipping } from '../../../redux/shipping/shipping.selectors';
import { resetPayment } from '../../../redux/payment/payment.actions';
import { selectCurrentPayment } from '../../../redux/payment/payment.selectors';

const API = runtimeConfig.REACT_APP_API_URI;

function CardRecovery({ attempt, order, refresh, report, busy, setBusy }) {
  const stripe = useStripe();
  const elements = useElements();
  const [complete, setComplete] = React.useState(false);
  const needsCard = !['requires_action', 'requires_confirmation'].includes(order.provider_status);
  const processing = ['processing', 'requires_capture'].includes(order.provider_status);
  const pay = async () => {
    setBusy(true);
    try {
      assertCurrentAttempt(attempt);
      const response = await axios.post(API + '/stripe/payment-intend', {
        order_id: order.id, order_ref: order.ref, checkout_token: attempt.token,
      });
      if (response.data.status !== 'success' || !response.data.secret) throw new Error('Unable to resume card payment.');
      const current = await stripe.retrievePaymentIntent(response.data.secret);
      if (current.error) throw current.error;
      if (!['succeeded', 'processing', 'requires_capture', 'canceled'].includes(current.paymentIntent.status)) {
        assertCurrentAttempt(attempt);
        const options = current.paymentIntent.status === 'requires_payment_method'
          ? { payment_method: { card: elements.getElement(CardElement) } } : undefined;
        const result = await stripe.confirmCardPayment(response.data.secret, options);
        if (result.error) throw result.error;
      }
      await refresh();
    } catch (error) {
      report(error.message || 'Card payment could not be completed. Your checkout is retained.');
    } finally { setBusy(false); }
  };
  if (processing) return <p>Your card payment is awaiting confirmation. Use “Check payment status” before paying again.</p>;
  return <div>
    {needsCard && <div className="form-group"><p>Enter your card details to continue this saved order.</p><CardElement onChange={e => setComplete(e.complete)} /></div>}
    <button className="btn btn-blue" disabled={busy || !stripe || !elements || (needsCard && !complete)} onClick={pay}>
      {busy ? 'Checking payment…' : 'Continue card payment'}
    </button>
  </div>;
}

function PaypalRecovery({ attempt, order, refresh, report, busy, setBusy }) {
  const capture = async data => {
    setBusy(true);
    try {
      assertCurrentAttempt(attempt);
      const response = await axios.post(API + '/paypal/' + order.id + '/capture', {
        order_ref: order.ref, paypal_order_id: data.orderID, checkout_token: attempt.token,
      });
      if (response.data.status !== 'success' || response.data.payment_status !== 'COMPLETED') throw new Error(response.data.message || 'PayPal confirmation is pending.');
      await refresh();
    } catch (error) { report(error.message || 'PayPal confirmation is pending. Check the payment status before paying again.'); }
    finally { setBusy(false); }
  };
  const create = async () => {
    assertCurrentAttempt(attempt);
    const response = await axios.post(API + '/paypal/create-order', {
      order_id: order.id, order_ref: order.ref, checkout_token: attempt.token,
    });
    if (response.data.status !== 'success' || !response.data.paypal_order_id) throw new Error('Unable to resume PayPal. Your checkout is retained.');
    return response.data.paypal_order_id;
  };
  if (!['pending', 'CREATED', 'SAVED', 'PAYER_ACTION_REQUIRED', 'APPROVED'].includes(order.provider_status)) return <p>PayPal is awaiting confirmation. Use “Check payment status” before paying again.</p>;
  if (order.provider_status === 'APPROVED' && order.paypal_order_id) return <button className="btn btn-blue" disabled={busy}
    onClick={() => capture({ orderID: order.paypal_order_id })}>Confirm saved PayPal payment</button>;
  return <PayPalScriptProvider options={{ 'client-id': runtimeConfig.REACT_APP_PAYPAL_CLIENT_ID, currency: 'GBP', intent: 'capture' }}>
    <PayPalButtons disabled={busy} createOrder={create} onApprove={capture}
      onCancel={() => report('PayPal was closed. Your saved checkout is still available.')}
      onError={() => report('PayPal is temporarily unavailable. Your saved checkout is retained.')} />
  </PayPalScriptProvider>;
}

export function CheckoutRecovery({ attempt, retryDraft, released, customer, items, cartTotal, shipping, payment, resetCart, resetCustomer, resetShipping, resetPayment }) {
  const history = useHistory();
  const [order, setOrder] = React.useState(null);
  const [missing, setMissing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const latestInputs = React.useRef(null);
  latestInputs.current = { customer, items, cartTotal, shipping, payment };
  const refresh = React.useCallback(async () => {
    setBusy(true); setError('');
    try {
      assertCurrentAttempt(attempt);
      const response = await axios.post(API + '/checkout/recover', { checkout_token: attempt.token });
      if (response.data.status !== 'success') throw new Error('Unable to check the saved checkout.');
      setOrder(response.data.data); setMissing(false);
    } catch (e) {
      if (e.response && e.response.status === 404 && e.response.data && e.response.data.code === 'CHECKOUT_NOT_FOUND') setMissing(true);
      else setError('Payment status is unavailable. Your saved checkout is retained; check again or contact the shop before paying.');
    } finally { setBusy(false); }
  }, [attempt]);
  React.useEffect(() => { refresh(); }, [refresh]);
  const releaseCancelled = () => {
    try { clearAttempt(attempt); released(); }
    catch (e) { setError(e.message || 'Your saved checkout changed. Reload to check its current status.'); }
  };
  const cancel = async () => {
    setBusy(true); setError('');
    try {
      assertCurrentAttempt(attempt);
      const response = await axios.post(API + '/checkout/cancel', { checkout_token: attempt.token });
      if (response.data.status !== 'success') throw new Error('Unable to cancel the saved checkout.');
      setOrder(response.data.data);
      if (response.data.data.state === 'cancelled') releaseCancelled();
    } catch (_) { setError('Cancellation could not be verified. Your saved checkout is retained; check its payment status before paying again.'); }
    finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true);
    try {
      const unchanged = await matchesBasket(attempt, () => checkoutPayload(latestInputs.current, order.method));
      clearAttempt(attempt);
      if (unchanged) { resetCart(); resetCustomer(); resetShipping(); resetPayment(); }
      history.push('/order-complete');
    } catch (_) { setError('Unable to finish this checkout. Your paid order is saved; check again before starting another payment.'); setBusy(false); }
  };
  return <section className="mb-4" aria-label="Saved checkout">
    <h2>Continue your saved checkout</h2>
    <p>These are the items and total saved when you started payment. We will check this order before starting another payment.</p>
    {busy && <p role="status">Checking your saved checkout…</p>}
    {error && <p role="alert">{error}</p>}
    {missing && <div><p>No saved order was found yet. Retry checkout using the same saved request.</p>
      <button className="btn btn-blue mr-2" disabled={busy} onClick={retryDraft}>Retry checkout</button>
      <button className="btn btn-outline-secondary" disabled={busy} onClick={cancel}>Cancel saved draft</button></div>}
    {order && <div>
      {order.id && <p>Order <strong>{order.ref}</strong> — <strong>£{Number(order.amount).toFixed(2)}</strong></p>}
      <ul>{order.items.map((item, index) => <li key={index}>{item.quantity} × {item.title}</li>)}</ul>
      {order.state === 'paid' && <div><p>This order has been paid. Any newer basket changes will be kept.</p><button className="btn btn-blue" disabled={busy} onClick={finish}>Continue</button></div>}
      {order.state === 'cancelled' && <button className="btn btn-blue" disabled={busy} onClick={releaseCancelled}>Return to checkout</button>}
      {order.state === 'closed' && <p>Please contact the shop about this order before paying again.</p>}
      {order.state === 'pending' && !error && <div>
        {order.method === 'stripe' && <Elements stripe={stripePromise}><CardRecovery {...{ attempt, order, refresh, busy, setBusy }} report={setError} /></Elements>}
        {order.method === 'paypal' && runtimeConfig.REACT_APP_PAYPAL_CLIENT_ID && <PaypalRecovery {...{ attempt, order, refresh, busy, setBusy }} report={setError} />}
      </div>}
      {order.state === 'pending' && <button className="btn btn-outline-secondary mr-2 mt-3" disabled={busy} onClick={cancel}>Cancel saved checkout</button>}
    </div>}
    <button className="btn btn-outline-secondary mr-2 mt-3" disabled={busy} onClick={refresh}>Check payment status</button>
  </section>;
}
export default connect(state => ({ customer: selectCurrentCustomer(state), items: selectCartItems(state), cartTotal: selectCartTotal(state),
  shipping: selectCurrentShipping(state), payment: selectCurrentPayment(state) }),
{ resetCart, resetCustomer, resetShipping, resetPayment })(CheckoutRecovery);
